import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError(t('forgotPassword.errors.emailRequired', 'Please enter your email address.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
      console.log('✅ Password reset email sent successfully');
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      
      let errorMessage = t('forgotPassword.errors.general', 'Failed to send password reset email.');
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = t('forgotPassword.errors.userNotFound', 'No account found with this email address.');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('forgotPassword.errors.invalidEmail', 'Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t('forgotPassword.errors.tooManyRequests', 'Too many requests. Please try again later.');
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = t('forgotPassword.errors.networkError', 'Network error. Please check your internet connection.');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('forgotPassword.success.title', 'Email Sent')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('forgotPassword.success.message', 'Password reset link has been sent to {email}. Please check your email to reset your password.', { email })}
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {t('forgotPassword.success.backToLogin', 'Back to Login')}
                </button>
                
                <button
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                    setError('');
                  }}
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {t('forgotPassword.success.sendAnother', 'Send to Another Email')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('forgotPassword.backToLogin', 'Back to Login')}
            </Link>
          </div>

          <div className="text-center mb-6">
            <Mail className="mx-auto h-12 w-12 text-primary-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">
              {t('forgotPassword.title', 'Forgot Password')}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {t('forgotPassword.subtitle', 'Enter your email address and we will send you a password reset link.')}
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('forgotPassword.emailLabel', 'Email Address')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={t('forgotPassword.emailPlaceholder', 'example@email.com')}
                />
              </div>
            </div>

            {error && (
                      <div className="rounded-md bg-slate-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-slate-400" />
            <div className="ml-3">
              <p className="text-sm text-slate-700">{error}</p>
            </div>
          </div>
        </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('forgotPassword.sending', 'Sending...')}
                  </div>
                ) : (
                  t('forgotPassword.sendButton', 'Send Password Reset Email')
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('forgotPassword.rememberPassword', 'Remember your password?')}{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('forgotPassword.signIn', 'Sign In')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 